import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation, Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  ArrowLeft,
  Calendar,
  Truck,
  Edit,
  Package,
  ShieldCheck,
  AlertCircle,
  Hash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { cn } from "@/lib/utils";

const editFormSchema = z.object({
  status: z.string().min(1, "Status is required"),
  carrier: z.string().optional(),
  trackingNumber: z.string().optional(),
  packingSlipNumber: z.string().optional(),
  notes: z.string().optional(),
});

type EditFormData = z.infer<typeof editFormSchema>;

const RECEIPT_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "posted", label: "Posted" },
  { value: "cancelled", label: "Cancelled" },
];

export default function GoodsReceiptDetailPage() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: receipt, isLoading, error } = useQuery<any>({
    queryKey: ["/api/goods-receipts", params.id],
  });

  const form = useForm<EditFormData>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      status: "draft",
      carrier: "",
      trackingNumber: "",
      packingSlipNumber: "",
      notes: "",
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: EditFormData) => {
      return apiRequest("PATCH", `/api/goods-receipts/${params.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goods-receipts", params.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/goods-receipts"] });
      toast({ title: "Goods receipt updated" });
      setEditOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update goods receipt", variant: "destructive" });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/goods-receipts/${params.id}/verify`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goods-receipts", params.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/goods-receipts"] });
      toast({ title: "Receipt verified" });
    },
    onError: () => toast({ title: "Failed to verify receipt", variant: "destructive" }),
  });

  const handleEditOpen = () => {
    if (receipt) {
      form.reset({
        status: receipt.status || "draft",
        carrier: receipt.carrier || "",
        trackingNumber: receipt.trackingNumber || "",
        packingSlipNumber: receipt.packingSlipNumber || "",
        notes: receipt.notes || "",
      });
    }
    setEditOpen(true);
  };

  const onSubmit = (data: EditFormData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-12 w-full rounded-lg" />
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">Goods receipt not found</p>
        <Button variant="outline" onClick={() => setLocation("/goods-receipts")}>
          Back to Goods Receipts
        </Button>
      </div>
    );
  }

  const lines = receipt.lines || [];

  const statusDotColor = (() => {
    switch (receipt.status) {
      case "draft": return "bg-yellow-500";
      case "posted": return "bg-green-500";
      case "cancelled": return "bg-red-500";
      default: return "bg-gray-400";
    }
  })();

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLocation("/goods-receipts")}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold tracking-tight">
                {receipt.receiptNumber}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-2xl font-bold tracking-tight pl-7 flex-wrap">
              {receipt.vendor && (
                <>
                  <Link
                    href={`/vendors/${receipt.vendorId}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {receipt.vendor.name}
                  </Link>
                  <span className="text-muted-foreground font-normal">/</span>
                </>
              )}
              <span className="text-muted-foreground font-normal">Status:</span>
              <span className="capitalize">{receipt.status}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {receipt.verificationPending && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => verifyMutation.mutate()}
                disabled={verifyMutation.isPending}
              >
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                Verify Receipt
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleEditOpen}>
              <Edit className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-0 border rounded-lg bg-card overflow-x-auto">
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <span className="text-xs text-muted-foreground">Status</span>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-2 w-2 rounded-full shrink-0", statusDotColor)} />
              <span className="text-sm font-medium capitalize">{receipt.status}</span>
            </div>
          </div>
          <div className="w-px h-8 bg-border" />
          <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <div>
              <span className="text-xs text-muted-foreground">Received</span>
              <div className="text-sm font-medium">
                {receipt.receivedAt ? format(new Date(receipt.receivedAt), "MMM d, yyyy") : "-"}
              </div>
            </div>
          </div>
          {receipt.carrier && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
                <Truck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground">Carrier</span>
                  <div className="text-sm font-medium">{receipt.carrier}</div>
                </div>
              </div>
            </>
          )}
          {receipt.trackingNumber && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
                <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground">Tracking</span>
                  <div className="text-sm font-medium font-mono">{receipt.trackingNumber}</div>
                </div>
              </div>
            </>
          )}
          {receipt.verificationPending && (
            <>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-2 px-4 py-2.5 whitespace-nowrap">
                <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground">Verification</span>
                  <Badge variant="destructive" className="text-xs gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Pending
                  </Badge>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Receipt Lines */}
      <div className="border rounded-lg bg-card">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold">Receipt Lines</h2>
            <Badge variant="secondary" className="ml-auto">{lines.length}</Badge>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Ordered</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">Rejected</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead>Lot #</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No receipt lines found
                  </TableCell>
                </TableRow>
              ) : (
                lines.map((line: any) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <div className="font-medium">{line.productName || line.description}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{line.orderedQuantity || "-"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{line.quantityReceived}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.quantityRejected > 0 ? (
                        <span className="text-red-600">{line.quantityRejected}</span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{line.uom}</TableCell>
                    <TableCell className="font-mono text-xs">{line.lotNumber || "-"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{line.notes || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit Drawer */}
      <FormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Goods Receipt"
        description="Update the goods receipt details"
        formId="edit-goods-receipt"
        isPending={updateMutation.isPending}
        submitLabel="Save Changes"
        size="md"
        data-testid="drawer-edit-goods-receipt"
      >
        <Form {...form}>
          <form
            id="edit-goods-receipt"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {RECEIPT_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="carrier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Carrier</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} placeholder="e.g., FedEx, UPS" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="trackingNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tracking Number</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} placeholder="Enter tracking number" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="packingSlipNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Packing Slip Number</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} placeholder="Enter packing slip number" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea variant="flat" {...field} rows={3} placeholder="Add notes..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
